import { useEffect, useState } from "react";
import SideMenu from "../General/SideMenu";
import ReactTable from "../General/ReactTable";
import { useDispatch } from "react-redux";
import { Show_MsgBox, setload_Screen } from "../ReduxStore/Message";
import { userApp } from "../Admin/Utility";

// ---------------------------------------------------------------------------
// GSTR-3B has no invoice-level report of its own -- on the real GST portal
// (and in Zoho Books) it is a COMPUTED SUMMARY RETURN, auto-populated from
// GSTR-1 (outward supplies -> Table 3.1) and GSTR-2B (purchase ITC -> Table 4).
// This screen therefore reuses the exact same RptIds/report calls as
// Gstr1.js and Gstr2b.js instead of duplicating report logic, then layers
// the 3B-specific tables (3.1 extras, Table 4 A/B, Table 5.1, Table 6.1)
// on top as manual-entry fields where no source report exists yet.
// ---------------------------------------------------------------------------

// Same RptIds as Gstr1.js
const RPT_OUTWARD = { B2B: 107, B2CL: 108, B2CS: 109, CDNR: 110, CDNUR: 111 };

// Same RptIds as Gstr2b.js (B2B/B2BUR/CDNR only -- HSN not needed for 3B totals)
const RPT_ITC = { B2B: 114, B2BUR: 115, CDNR: 117 };

// Subset of Gstr1.js's CATEGORY_FIELD_MAP, plus a head-level split where the
// underlying report actually carries it. Only B2B carries a real IGST/CGST/SGST
// split today -- B2CL/B2CS store tax as a single misleadingly-named "Tax Rate"
// column (confirmed in Gstr1.js), so their tax cannot be auto-split by head.
const OUTWARD_MAP = {
  B2B: {
    taxable: "Taxable Value",
    headFields: { igst: "Integrated Tax Paid-IGST", cgst: "Central Tax Paid-CGST", sgst: "State/UT Tax Paid-SGST" },
    hasHeadSplit: true,
    isReduction: false,
  },
  B2CL: {
    taxable: "Taxable Value",
    taxFields: ["Tax Rate"], // holds tax amount, not a rate -- see Gstr1.js
    hasHeadSplit: false,
    isReduction: false,
  },
  B2CS: {
    taxable: "Taxable Value",
    taxFields: ["Tax Rate"],
    hasHeadSplit: false,
    isReduction: false,
  },
  CDNR: {
    taxable: "Taxable Value",
    noteValue: "Note Value",
    cess: "Cess Amount",
    hasHeadSplit: false,
    isReduction: true, // net credit notes reduce outward liability
  },
  CDNUR: {
    taxable: "Taxable Value",
    noteValue: "Note Value",
    cess: "Cess Amount",
    hasHeadSplit: false,
    isReduction: true,
  },
};

// Same field names as Gstr2b.js's CATEGORY_FIELD_MAP
const ITC_MAP = {
  B2B: {
    taxable: "TaxableAmt",
    headFields: { igst: "ITCIgst", cgst: "ITCCgst", sgst: "ITCSGst", cess: "ITCCess" },
    eligibility: "ITCEligibility",
    isReduction: false,
    confirmed: false, // same caveat as Gstr2b.js -- not independently confirmed
  },
  B2BUR: {
    taxable: "TaxableAmt",
    headFields: { igst: "ITCIgst", cgst: "ITCCgst", sgst: "ITCSGst", cess: "ITCCess" },
    eligibility: "ITCEligibility",
    isReduction: false,
    confirmed: true,
  },
  CDNR: {
    taxable: "TaxableAmt",
    headFields: { igst: "ITCIgst", cgst: "ITCCgst", sgst: "ITCSGst", cess: "ITCCess" },
    eligibility: "ITCEligibility",
    isReduction: true, // credit notes reduce ITC already claimed
    confirmed: true,
  },
};

const HEADS = ["igst", "cgst", "sgst", "cess"];
const ZERO_HEADS = { igst: 0, cgst: 0, sgst: 0, cess: 0 };

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function Gstr3b() {
  const dispatch = useDispatch();

  const today = new Date();
  const [iMonth, setiMonth] = useState(today.getMonth() + 1);
  const [iYear, setiYear] = useState(today.getFullYear());

  const [strActiveTab, setstrActiveTab] = useState("Outward"); // Outward | ITC | Payment | Push | File
  const [strFilingStatus, setstrFilingStatus] = useState("Unfiled"); // Unfiled | Pushed | Filed

  // ---- Auto-pulled raw + design (for the optional "view invoices" tables) ----
  const [objOutwardRaw, setobjOutwardRaw] = useState({ B2B: [], B2CL: [], B2CS: [], CDNR: [], CDNUR: [] });
  const [objOutwardDesign, setobjOutwardDesign] = useState({ B2B: [], B2CL: [], B2CS: [], CDNR: [], CDNUR: [] });
  const [objOutwardSummary, setobjOutwardSummary] = useState({});
  const [strActiveOutwardCard, setstrActiveOutwardCard] = useState("B2B");

  const [objITCRaw, setobjITCRaw] = useState({ B2B: [], B2BUR: [], CDNR: [] });
  const [objITCDesign, setobjITCDesign] = useState({ B2B: [], B2BUR: [], CDNR: [] });
  const [objITCSummary, setobjITCSummary] = useState({});
  const [strActiveITCCard, setstrActiveITCCard] = useState("B2B");

  // ---- Table 3.1 -- rows the source reports can't provide, entered by the user ----
  const [objZeroRated, setobjZeroRated] = useState({ taxable: 0, igst: 0 });
  const [objNilExempt, setobjNilExempt] = useState({ taxable: 0 });
  const [objNonGST, setobjNonGST] = useState({ taxable: 0 });
  const [objRCMInward, setobjRCMInward] = useState({ taxable: 0, igst: 0, cgst: 0, sgst: 0 });

  // Head-level split for the regular-taxable line (3.1a). Only B2B's share is
  // known from the source report -- the rest ("Unallocated") must be entered
  // by the user so the total ties back to the invoice-level tax figure.
  const [objOutwardHeadSplit, setobjOutwardHeadSplit] = useState({ ...ZERO_HEADS });

  // ---- Table 4A rows 1-4 -- not present in the GSTR-2B purchase reports ----
  const [objImportGoods, setobjImportGoods] = useState({ igst: 0, cess: 0 });
  const [objImportServices, setobjImportServices] = useState({ igst: 0, cess: 0 });
  const [objRCMClaim, setobjRCMClaim] = useState({ ...ZERO_HEADS });
  const [objISDClaim, setobjISDClaim] = useState({ ...ZERO_HEADS });

  // ---- Table 4B -- ITC reversal ----
  const [objReversalRule, setobjReversalRule] = useState({ ...ZERO_HEADS });
  const [objReversalOthers, setobjReversalOthers] = useState({ ...ZERO_HEADS });

  // ---- Table 5.1 -- interest & late fee ----
  const [objInterest, setobjInterest] = useState({ ...ZERO_HEADS });
  const [objLateFee, setobjLateFee] = useState({ cgst: 0, sgst: 0 }); // no IGST/Cess leg per GST rules

  // ---- Table 6.1 -- cash ledger balance (placeholder pending real ledger API) ----
  const [objCashLedger, setobjCashLedger] = useState({ ...ZERO_HEADS });

  const [arrValidationErrors, setarrValidationErrors] = useState([]);

  // ---- OTP / Auth flow (same pattern as Gstr1.js / EInvoice / EWayBill) ----
  const [isOTPModal, setisOTPModal] = useState(false);
  const [strOTP, setstrOTP] = useState("");
  const [strAuthToken, setstrAuthToken] = useState("");
  const [strAckNo, setstrAckNo] = useState("");

  useEffect(() => {
    Load_3BData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iMonth, iYear]);

  const AlertMessage = (Msg, type) => {
    dispatch(Show_MsgBox({ Message: Msg, Type: type, isMsg: true }));
  };

  const JsontoArray = (DataArray) => (DataArray !== null ? JSON.parse(DataArray) : []);
  const fnNum = (v) => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };
  const fnFormatCurrency = (val) =>
    "\u20B9" + Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  // Pulls the 5 GSTR-1 section reports + 3 GSTR-2B section reports in
  // parallel -- same generic Reports/Load_GeneralReports endpoint, same
  // objBase shape, as Gstr1.js and Gstr2b.js. No new backend report work
  // needed for the auto-computed parts of this screen.
  const Load_3BData = async () => {
    try {
      await dispatch(setload_Screen(true));
      const strFromDate = `${iYear}-${String(iMonth).padStart(2, "0")}-01`;
      const objBase = {
        iPartyId: 0,
        strColName: "",
        strPartyName: "",
        strSearch: "",
        strCompanyDB: localStorage.db,
        strMasterDB: localStorage.Msdb,
        iUserId: Number(localStorage.UserId),
        CurrtPage: 1,
        strFromDate: strFromDate,
        iMonth: iMonth,
        iYear: iYear,
      };

      const outwardKeys = Object.keys(RPT_OUTWARD);
      const itcKeys = Object.keys(RPT_ITC);

      const outwardCalls = outwardKeys.map((key) =>
        userApp.post(`Reports/Load_GeneralReports/`, { ...objBase, iRptId: RPT_OUTWARD[key] })
      );
      const itcCalls = itcKeys.map((key) =>
        userApp.post(`Reports/Load_GeneralReports/`, { ...objBase, iRptId: RPT_ITC[key] })
      );

      const [outwardResults, itcResults] = await Promise.all([
        Promise.all(outwardCalls),
        Promise.all(itcCalls),
      ]);

      const newOutRaw = {}, newOutDesign = {}, newOutSummary = {};
      outwardResults.forEach((Res, idx) => {
        const key = outwardKeys[idx];
        if (Res.data.strStatus === "Success") {
          const rows = JsontoArray(Res.data.EntReport);
          newOutRaw[key] = rows;
          newOutDesign[key] = (Res.data.EntDesign || []).sort((a, b) => a.inOrderTo - b.inOrderTo);
          newOutSummary[key] = Summarize_Outward(key, rows);
        } else {
          newOutRaw[key] = [];
          newOutDesign[key] = [];
          newOutSummary[key] = { count: 0, taxable: 0, tax: 0, head: { ...ZERO_HEADS } };
        }
      });

      const newITCRaw = {}, newITCDesign = {}, newITCSummary = {};
      itcResults.forEach((Res, idx) => {
        const key = itcKeys[idx];
        if (Res.data.strStatus === "Success") {
          const rows = JsontoArray(Res.data.EntReport);
          newITCRaw[key] = rows;
          newITCDesign[key] = (Res.data.EntDesign || []).sort((a, b) => a.inOrderTo - b.inOrderTo);
          newITCSummary[key] = Summarize_ITC(key, rows);
        } else {
          newITCRaw[key] = [];
          newITCDesign[key] = [];
          newITCSummary[key] = { count: 0, eligible: { ...ZERO_HEADS }, ineligible: { ...ZERO_HEADS } };
        }
      });

      setobjOutwardRaw(newOutRaw);
      setobjOutwardDesign(newOutDesign);
      setobjOutwardSummary(newOutSummary);
      setobjITCRaw(newITCRaw);
      setobjITCDesign(newITCDesign);
      setobjITCSummary(newITCSummary);

      // Pre-fill the only head split we actually know (B2B) as a starting
      // point -- everything else stays 0 / "Unallocated" until the user
      // confirms it, same honesty convention as the confirmed:false flags.
      const b2bHead = newOutSummary.B2B?.head || { ...ZERO_HEADS };
      setobjOutwardHeadSplit({ igst: b2bHead.igst, cgst: b2bHead.cgst, sgst: b2bHead.sgst, cess: 0 });
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      dispatch(setload_Screen(false));
    }
  };

  // ---- Outward (3.1) summarization ----
  const Summarize_Outward = (key, rows) => {
    const map = OUTWARD_MAP[key];
    let taxable = 0, tax = 0;
    const head = { ...ZERO_HEADS };
    rows.forEach((r) => {
      const rowTaxable = fnNum(r[map.taxable]);
      taxable += rowTaxable;
      if (map.hasHeadSplit) {
        const igst = fnNum(r[map.headFields.igst]);
        const cgst = fnNum(r[map.headFields.cgst]);
        const sgst = fnNum(r[map.headFields.sgst]);
        head.igst += igst; head.cgst += cgst; head.sgst += sgst;
        tax += igst + cgst + sgst;
      } else if (map.taxFields) {
        map.taxFields.forEach((f) => { tax += fnNum(r[f]); });
      } else if (map.noteValue) {
        const cess = map.cess ? fnNum(r[map.cess]) : 0;
        tax += fnNum(r[map.noteValue]) - rowTaxable - cess;
      }
    });
    if (map.isReduction) { taxable = -taxable; tax = -tax; }
    return { count: rows.length, taxable, tax, head };
  };

  // ---- ITC (Table 4) summarization -- splits eligible vs ineligible by head ----
  const Summarize_ITC = (key, rows) => {
    const map = ITC_MAP[key];
    const eligible = { ...ZERO_HEADS };
    const ineligible = { ...ZERO_HEADS };
    rows.forEach((r) => {
      const isEligible = r[map.eligibility] === "Eligible";
      const bucket = isEligible ? eligible : ineligible;
      const sign = map.isReduction ? -1 : 1;
      HEADS.forEach((h) => {
        bucket[h] += sign * fnNum(r[map.headFields[h]]);
      });
    });
    return { count: rows.length, eligible, ineligible };
  };

  // ---- Aggregate totals used across Table 3.1 / 4 / 5.1 / 6.1 ----
  const outwardTaxableTotal = Object.values(objOutwardSummary).reduce((s, o) => s + (o.taxable || 0), 0);
  const outwardTaxTotal = Object.values(objOutwardSummary).reduce((s, o) => s + (o.tax || 0), 0);
  const outwardHeadSplitTotal = HEADS.reduce((s, h) => s + fnNum(objOutwardHeadSplit[h]), 0);
  const outwardUnallocated = outwardTaxTotal + fnNum(objRCMInward.igst) + fnNum(objRCMInward.cgst) + fnNum(objRCMInward.sgst)
    - outwardHeadSplitTotal;

  const itcAllOther = HEADS.reduce((acc, h) => {
    acc[h] = Object.values(objITCSummary).reduce((s, o) => s + fnNum(o.eligible?.[h]), 0);
    return acc;
  }, { ...ZERO_HEADS });
  const itcIneligibleTotal = HEADS.reduce((acc, h) => {
    acc[h] = Object.values(objITCSummary).reduce((s, o) => s + fnNum(o.ineligible?.[h]), 0);
    return acc;
  }, { ...ZERO_HEADS });

  // Table 4A total = rows 1-4 (manual) + row 5 (auto "all other ITC")
  const itcAvailableTotal = HEADS.reduce((acc, h) => {
    acc[h] =
      fnNum(objImportGoods[h]) + fnNum(objImportServices[h]) +
      fnNum(objRCMClaim[h]) + fnNum(objISDClaim[h]) + fnNum(itcAllOther[h]);
    return acc;
  }, { ...ZERO_HEADS });

  const itcReversalTotal = HEADS.reduce((acc, h) => {
    acc[h] = fnNum(objReversalRule[h]) + fnNum(objReversalOthers[h]);
    return acc;
  }, { ...ZERO_HEADS });

  // Table 4C -- Net ITC available
  const netITC = HEADS.reduce((acc, h) => {
    acc[h] = itcAvailableTotal[h] - itcReversalTotal[h];
    return acc;
  }, { ...ZERO_HEADS });

  // Table 3.1(a)+(d) liability by head, before set-off
  const payableByHead = {
    igst: fnNum(objOutwardHeadSplit.igst) + fnNum(objRCMInward.igst),
    cgst: fnNum(objOutwardHeadSplit.cgst) + fnNum(objRCMInward.cgst),
    sgst: fnNum(objOutwardHeadSplit.sgst) + fnNum(objRCMInward.sgst),
    cess: fnNum(objOutwardHeadSplit.cess),
  };

  // Table 6.1 -- ITC set-off in Rule 88A order:
  //  1. IGST ITC first offsets IGST liability, remainder goes to CGST then SGST
  //  2. CGST ITC offsets CGST liability first, remainder (if any) to IGST
  //  3. SGST ITC offsets SGST liability first, remainder (if any) to IGST
  //     (SGST ITC can never be used against CGST liability)
  //  4. Cess ITC only offsets Cess liability
  const Compute_SetOff = () => {
    let liab = { ...payableByHead };
    let itc = { igst: fnNum(netITC.igst), cgst: fnNum(netITC.cgst), sgst: fnNum(netITC.sgst), cess: fnNum(netITC.cess) };
    const used = { igst: 0, cgst: 0, sgst: 0, cess: 0 };

    // Step 1: IGST ITC -> IGST liability
    let take = Math.min(itc.igst, liab.igst);
    used.igst += take; liab.igst -= take; itc.igst -= take;
    // remainder IGST ITC -> CGST liability
    take = Math.min(itc.igst, liab.cgst);
    used.igst += take; liab.cgst -= take; itc.igst -= take;
    // remainder IGST ITC -> SGST liability
    take = Math.min(itc.igst, liab.sgst);
    used.igst += take; liab.sgst -= take; itc.igst -= take;

    // Step 2: CGST ITC -> CGST liability, then IGST liability
    take = Math.min(itc.cgst, liab.cgst);
    used.cgst += take; liab.cgst -= take; itc.cgst -= take;
    take = Math.min(itc.cgst, liab.igst);
    used.cgst += take; liab.igst -= take; itc.cgst -= take;

    // Step 3: SGST ITC -> SGST liability, then IGST liability
    take = Math.min(itc.sgst, liab.sgst);
    used.sgst += take; liab.sgst -= take; itc.sgst -= take;
    take = Math.min(itc.sgst, liab.igst);
    used.sgst += take; liab.igst -= take; itc.sgst -= take;

    // Step 4: Cess ITC -> Cess liability only
    take = Math.min(itc.cess, liab.cess);
    used.cess += take; liab.cess -= take; itc.cess -= take;

    return { cashPayable: liab, itcUtilized: used, itcCarriedForward: itc };
  };

  const setOff = Compute_SetOff();
  const interestLateFeeTotal = HEADS.reduce((s, h) => s + fnNum(objInterest[h]), 0) + fnNum(objLateFee.cgst) + fnNum(objLateFee.sgst);
  const totalCashRequired =
    HEADS.reduce((s, h) => s + fnNum(setOff.cashPayable[h]), 0) +
    HEADS.reduce((s, h) => s + fnNum(objInterest[h]), 0) +
    fnNum(objLateFee.cgst) + fnNum(objLateFee.sgst);
  const totalCashLedger = HEADS.reduce((s, h) => s + fnNum(objCashLedger[h]), 0);
  const netCashToPay = Math.max(0, totalCashRequired - totalCashLedger);

  // ---- Validation gate before Push (mirrors Gstr1.js's HSN validation gate) ----
  const Validate_GSTR3B = () => {
    const errors = [];
    if (Math.abs(outwardUnallocated) > 1) {
      errors.push({ issue: `Table 3.1 head split is unbalanced by ${fnFormatCurrency(outwardUnallocated)} -- IGST + CGST + SGST + Cess must equal the total outward tax before pushing.` });
    }
    setarrValidationErrors(errors);
    return errors;
  };

  useEffect(() => {
    Validate_GSTR3B();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objOutwardHeadSplit, objOutwardSummary, objRCMInward]);

  const Copy_RCM_To_ITCClaim = () => {
    setobjRCMClaim((prev) => ({
      ...prev,
      igst: fnNum(objRCMInward.igst), cgst: fnNum(objRCMInward.cgst), sgst: fnNum(objRCMInward.sgst),
    }));
    AlertMessage("RCM liability copied into Table 4 ITC claim -- review before saving", "info");
  };

  // ---- OTP + Push to GSTN flow (same pattern as Gstr1.js) ----
  const Request_OTP = async () => {
    try {
      await dispatch(setload_Screen(true));
      const objReq = {
        iMonth, iYear,
        strCompanyDB: localStorage.db,
        iUserId: Number(localStorage.UserId),
      };
      // TODO (backend): TaxProGSTRBAL.RequestOTP() -- same GSTN OTP flow as GSTR-1
      await userApp.post(`GSTR3B/Request_OTP`, objReq).then((Res) => {
        if (Res.data.strStatus === "Success") {
          setisOTPModal(true);
        } else {
          AlertMessage(Res.data.strMessage, "error");
        }
      });
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      dispatch(setload_Screen(false));
    }
  };

  const Verify_OTP_PushGSTN = async () => {
    try {
      if (!strOTP) {
        AlertMessage("Please Enter OTP", "info");
        return;
      }
      await dispatch(setload_Screen(true));
      const objVerify = {
        strOTP, iMonth, iYear,
        objPayload: {
          outward: { taxable: outwardTaxableTotal, head: objOutwardHeadSplit, rcmInward: objRCMInward, zeroRated: objZeroRated, nilExempt: objNilExempt, nonGST: objNonGST },
          itc: { allOther: itcAllOther, importGoods: objImportGoods, importServices: objImportServices, rcmClaim: objRCMClaim, isdClaim: objISDClaim, reversal: { rule: objReversalRule, others: objReversalOthers }, netITC },
          payment: { setOff, interest: objInterest, lateFee: objLateFee, cashLedger: objCashLedger, netCashToPay },
        },
        strCompanyDB: localStorage.db,
        iUserId: Number(localStorage.UserId),
      };
      // TODO (backend): TaxProGSTRBAL.GetAuthToken(otp) then PushGSTR3B(jsonPayload)
      await userApp.post(`GSTR3B/Verify_OTP_And_Push`, objVerify).then((Res) => {
        if (Res.data.strStatus === "Success") {
          setstrAuthToken(Res.data.AuthToken);
          setstrFilingStatus("Pushed");
          setisOTPModal(false);
          setstrOTP("");
          AlertMessage("GSTR-3B data pushed to GSTN successfully", "success");
        } else {
          AlertMessage(Res.data.strMessage, "error");
        }
      });
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      dispatch(setload_Screen(false));
    }
  };

  const File_Return = async () => {
    try {
      await dispatch(setload_Screen(true));
      const objFile = {
        iMonth, iYear, strAuthToken,
        strCompanyDB: localStorage.db,
        iUserId: Number(localStorage.UserId),
      };
      // TODO (backend): TaxProGSTRBAL.FileReturn() -- final submit, returns ARN
      // NOTE: real GSTR-3B filing debits the cash/credit ledgers for netCashToPay
      // -- confirm with TaxPro whether that debit happens here or must be a
      // separate "Create Challan" step before this call.
      await userApp.post(`GSTR3B/File_Return`, objFile).then((Res) => {
        if (Res.data.strStatus === "Success") {
          setstrAckNo(Res.data.AckNo);
          setstrFilingStatus("Filed");
          AlertMessage("GSTR-3B filed successfully", "success");
        } else {
          AlertMessage(Res.data.strMessage, "error");
        }
      });
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      dispatch(setload_Screen(false));
    }
  };

  const getStatusPillClass = () => {
    if (strFilingStatus === "Filed") return "gstr1-pill gstr1-pill-success";
    if (strFilingStatus === "Pushed") return "gstr1-pill gstr1-pill-info";
    return "gstr1-pill gstr1-pill-warning";
  };

  // Same dynamic-column approach as Gstr1.js / Gstr2b.js
  const Build_Columns = (design) => {
    if (!design || design.length === 0) return [];
    return design
      .filter((d) => d.isShowInReport)
      .map((d) => ({
        Header: d.strAliasName,
        accessor: d.strRptColName,
        Datatype: d.strDatatype === "decimal" ? "Decimal" : undefined,
        minWidth: d.iRptColLen,
        style: { justifyContent: d.iFontAlign === 3 ? "right" : "left" },
      }));
  };

  const OUTWARD_CARD_META = [
    { key: "B2B", label: "B2B invoices" },
    { key: "B2CL", label: "B2C large" },
    { key: "B2CS", label: "B2C small" },
    { key: "CDNR", label: "Credit/debit notes" },
    { key: "CDNUR", label: "CDN unregistered" },
  ];
  const ITC_CARD_META = [
    { key: "B2B", label: "B2B purchases" },
    { key: "B2BUR", label: "B2B unregistered" },
    { key: "CDNR", label: "Credit/debit notes" },
  ];

  // A single head-quad numeric input row -- reused across Table 3.1/4/5.1/6.1
  const HeadInputRow = ({ label, value, onChange, heads = HEADS, readOnlyHeads = [] }) => (
    <div className="row gstr1-btn-margin">
      <div className="col-sm-12 col-md-3"><label>{label}</label></div>
      {HEADS.map((h) =>
        heads.includes(h) ? (
          <div className="col-sm-6 col-md-2" key={h}>
            <label style={{ textTransform: "uppercase", fontSize: "11px" }}>{h}</label>
            <input
              type="number"
              className="form-control"
              value={value[h] ?? 0}
              disabled={readOnlyHeads.includes(h)}
              onChange={(e) => onChange({ ...value, [h]: e.target.value === "" ? 0 : Number(e.target.value) })}
            />
          </div>
        ) : null
      )}
    </div>
  );

  return (
    <>
      <SideMenu ScrId={116} />
      <div className="gstr1-wrap">
        <div className="gstr1-header">
          <div>
            <h2 className="gstr1-title">GSTR-3B Filing</h2>
            <p className="gstr1-subtitle">Monthly summary of outward supplies, ITC and tax payment</p>
          </div>
          <div className="gstr1-header-actions">
            <select className="form-control gstr1-period-select" value={iMonth} onChange={(e) => setiMonth(Number(e.target.value))}>
              {MONTHS.map((m, idx) => (<option key={idx} value={idx + 1}>{m}</option>))}
            </select>
            <select className="form-control gstr1-period-select" value={iYear} onChange={(e) => setiYear(Number(e.target.value))}>
              {[iYear - 1, iYear, iYear + 1].map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <span className={getStatusPillClass()}>{strFilingStatus}</span>
          </div>
        </div>

        {/* Persistent summary strip, visible on every tab -- same idea as Zoho's GSTR-3B header */}
        <div className="gstr1-card-grid">
          <div className="gstr1-card">
            <i className="fa-solid fa-arrow-up-from-bracket"></i>
            <p className="gstr1-card-label">Outward tax liability</p>
            <p className="gstr1-card-count">{fnFormatCurrency(payableByHead.igst + payableByHead.cgst + payableByHead.sgst + payableByHead.cess)}</p>
          </div>
          <div className="gstr1-card">
            <i className="fa-solid fa-circle-check"></i>
            <p className="gstr1-card-label">Net eligible ITC</p>
            <p className="gstr1-card-count">{fnFormatCurrency(netITC.igst + netITC.cgst + netITC.sgst + netITC.cess)}</p>
          </div>
          <div className="gstr1-card">
            <i className="fa-solid fa-hand-holding-dollar"></i>
            <p className="gstr1-card-label">Interest &amp; late fee</p>
            <p className="gstr1-card-count">{fnFormatCurrency(interestLateFeeTotal)}</p>
          </div>
          <div className="gstr1-card">
            <i className="fa-solid fa-money-bill-wave"></i>
            <p className="gstr1-card-label">Net cash payable</p>
            <p className="gstr1-card-count">{fnFormatCurrency(netCashToPay)}</p>
          </div>
        </div>

        <div className="gstr1-tabs">
          <div className={`gstr1-tab ${strActiveTab === "Outward" ? "active" : ""}`} onClick={() => setstrActiveTab("Outward")}>1. Outward Supplies (3.1)</div>
          <div className={`gstr1-tab ${strActiveTab === "ITC" ? "active" : ""}`} onClick={() => setstrActiveTab("ITC")}>2. Eligible ITC (Table 4)</div>
          <div className={`gstr1-tab ${strActiveTab === "Payment" ? "active" : ""}`} onClick={() => setstrActiveTab("Payment")}>3. Tax Payment (5.1 &amp; 6.1)</div>
          <div className={`gstr1-tab ${strActiveTab === "Push" ? "active" : ""}`} onClick={() => setstrActiveTab("Push")}>4. Push to GSTN</div>
          <div className={`gstr1-tab ${strActiveTab === "File" ? "active" : ""}`} onClick={() => setstrActiveTab("File")}>5. File Return</div>
        </div>

        {/* ===================== TAB 1: OUTWARD SUPPLIES (3.1) ===================== */}
        {strActiveTab === "Outward" && (
          <>
            <div className="gstr1-panel">
              <h3 className="gstr1-panel-title">3.1(a) Outward taxable supplies (auto, from GSTR-1)</h3>
              <table className="gstr1-summary-table">
                <thead><tr><th>Category</th><th>Records</th><th>Taxable value</th><th>Tax</th></tr></thead>
                <tbody>
                  {OUTWARD_CARD_META.map((c) => (
                    <tr key={c.key} className={strActiveOutwardCard === c.key ? "gstr1-card-active" : ""} onClick={() => setstrActiveOutwardCard(c.key)} style={{ cursor: "pointer" }}>
                      <td>{c.label}{OUTWARD_MAP[c.key].isReduction ? " (reduces total)" : ""}</td>
                      <td>{objOutwardSummary[c.key]?.count || 0}</td>
                      <td>{fnFormatCurrency(objOutwardSummary[c.key]?.taxable)}</td>
                      <td>{fnFormatCurrency(objOutwardSummary[c.key]?.tax)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold" }}>
                    <td>Net total</td><td>-</td><td>{fnFormatCurrency(outwardTaxableTotal)}</td><td>{fnFormatCurrency(outwardTaxTotal)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="gstr1-detail-table">
                <ReactTable
                  columns={Build_Columns(objOutwardDesign[strActiveOutwardCard])}
                  data={objOutwardRaw[strActiveOutwardCard] || []}
                  minRows={3}
                  row_click={() => {}}
                  row_doubleclick={() => {}}
                  background={true}
                  className="full-table"
                />
              </div>

              <hr />
              <h3 className="gstr1-panel-title">3.1(a) Head-wise split (confirm/enter)</h3>
              <p className="gstr1-help-text">Only the B2B share above carries a real IGST/CGST/SGST split from the source report. Distribute the remainder below -- it must add up to the net tax total ({fnFormatCurrency(outwardTaxTotal + objRCMInward.igst + objRCMInward.cgst + objRCMInward.sgst)}) before you can push.</p>
              <HeadInputRow label="Tax amount by head" value={objOutwardHeadSplit} onChange={setobjOutwardHeadSplit} />
              <p className={outwardUnallocated === 0 ? "gstr1-help-text" : "gstr1-error-text"}>
                Unallocated: {fnFormatCurrency(outwardUnallocated)}
              </p>

              <hr />
              <h3 className="gstr1-panel-title">3.1(b)-(e) Manual entries (not in invoice reports)</h3>
              <div className="row">
                <div className="col-sm-6 col-md-3">
                  <label>Zero rated (exports/SEZ) -- taxable</label>
                  <input type="number" className="form-control" value={objZeroRated.taxable} onChange={(e) => setobjZeroRated({ ...objZeroRated, taxable: Number(e.target.value) || 0 })} />
                </div>
                <div className="col-sm-6 col-md-3">
                  <label>Zero rated -- IGST</label>
                  <input type="number" className="form-control" value={objZeroRated.igst} onChange={(e) => setobjZeroRated({ ...objZeroRated, igst: Number(e.target.value) || 0 })} />
                </div>
                <div className="col-sm-6 col-md-3">
                  <label>Nil rated / exempted -- taxable</label>
                  <input type="number" className="form-control" value={objNilExempt.taxable} onChange={(e) => setobjNilExempt({ taxable: Number(e.target.value) || 0 })} />
                </div>
                <div className="col-sm-6 col-md-3">
                  <label>Non-GST outward -- value</label>
                  <input type="number" className="form-control" value={objNonGST.taxable} onChange={(e) => setobjNonGST({ taxable: Number(e.target.value) || 0 })} />
                </div>
              </div>

              <h4 className="gstr1-panel-title" style={{ fontSize: "15px" }}>3.1(d) Inward supplies liable to RCM</h4>
              <HeadInputRow label="RCM inward liability" value={objRCMInward} onChange={setobjRCMInward} heads={["igst", "cgst", "sgst"]} />
              <p className="gstr1-help-text">This amount is added to your outward liability (payable in cash) and can usually be claimed back as ITC the same month.</p>
              <button className="btn-fabgreen gstr1-btn-margin" onClick={Copy_RCM_To_ITCClaim}>Copy to Table 4 ITC claim&nbsp;<i className="fa-solid fa-copy"></i></button>
            </div>
          </>
        )}

        {/* ===================== TAB 2: ELIGIBLE ITC (Table 4) ===================== */}
        {strActiveTab === "ITC" && (
          <div className="gstr1-panel">
            <h3 className="gstr1-panel-title">4A(5) All other ITC (auto, from GSTR-2B)</h3>
            <table className="gstr1-summary-table">
              <thead><tr><th>Category</th><th>Records</th><th>IGST</th><th>CGST</th><th>SGST</th><th>Cess</th></tr></thead>
              <tbody>
                {ITC_CARD_META.map((c) => (
                  <tr key={c.key} className={strActiveITCCard === c.key ? "gstr1-card-active" : ""} onClick={() => setstrActiveITCCard(c.key)} style={{ cursor: "pointer" }}>
                    <td>{c.label}{ITC_MAP[c.key].isReduction ? " (reduces ITC)" : ""}</td>
                    <td>{objITCSummary[c.key]?.count || 0}</td>
                    <td>{fnFormatCurrency(objITCSummary[c.key]?.eligible?.igst)}</td>
                    <td>{fnFormatCurrency(objITCSummary[c.key]?.eligible?.cgst)}</td>
                    <td>{fnFormatCurrency(objITCSummary[c.key]?.eligible?.sgst)}</td>
                    <td>{fnFormatCurrency(objITCSummary[c.key]?.eligible?.cess)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: "bold" }}>
                  <td>Net total</td><td>-</td>
                  <td>{fnFormatCurrency(itcAllOther.igst)}</td><td>{fnFormatCurrency(itcAllOther.cgst)}</td>
                  <td>{fnFormatCurrency(itcAllOther.sgst)}</td><td>{fnFormatCurrency(itcAllOther.cess)}</td>
                </tr>
              </tbody>
            </table>

            {!ITC_MAP.B2B.confirmed && (
              <div className="gstr1-alert-banner">
                <i className="fa-solid fa-triangle-exclamation"></i>
                Column names for "B2B" (RptId {RPT_ITC.B2B}) are not independently confirmed -- share a sample response to verify before relying on this total.
              </div>
            )}

            <div className="gstr1-detail-table">
              <ReactTable
                columns={Build_Columns(objITCDesign[strActiveITCCard])}
                data={objITCRaw[strActiveITCCard] || []}
                minRows={3}
                row_click={() => {}}
                row_doubleclick={() => {}}
                background={true}
                className="full-table"
              />
            </div>

            <hr />
            <h3 className="gstr1-panel-title">4A(1)-(4) Manual entries (not in purchase reports yet)</h3>
            <HeadInputRow label="Import of goods" value={objImportGoods} onChange={setobjImportGoods} heads={["igst", "cess"]} />
            <HeadInputRow label="Import of services" value={objImportServices} onChange={setobjImportServices} heads={["igst", "cess"]} />
            <HeadInputRow label="Inward supplies liable to RCM" value={objRCMClaim} onChange={setobjRCMClaim} />
            <HeadInputRow label="ITC from Input Service Distributor" value={objISDClaim} onChange={setobjISDClaim} />

            <h3 className="gstr1-panel-title">4B ITC reversed</h3>
            <HeadInputRow label="As per Rule 42 &amp; 43" value={objReversalRule} onChange={setobjReversalRule} />
            <HeadInputRow label="Others" value={objReversalOthers} onChange={setobjReversalOthers} />

            <table className="gstr1-summary-table gstr1-btn-margin">
              <thead><tr><th></th><th>IGST</th><th>CGST</th><th>SGST</th><th>Cess</th></tr></thead>
              <tbody>
                <tr><td>4A Total ITC available</td><td>{fnFormatCurrency(itcAvailableTotal.igst)}</td><td>{fnFormatCurrency(itcAvailableTotal.cgst)}</td><td>{fnFormatCurrency(itcAvailableTotal.sgst)}</td><td>{fnFormatCurrency(itcAvailableTotal.cess)}</td></tr>
                <tr><td>4B Total ITC reversed</td><td>{fnFormatCurrency(itcReversalTotal.igst)}</td><td>{fnFormatCurrency(itcReversalTotal.cgst)}</td><td>{fnFormatCurrency(itcReversalTotal.sgst)}</td><td>{fnFormatCurrency(itcReversalTotal.cess)}</td></tr>
                <tr style={{ fontWeight: "bold" }}><td>4C Net ITC available</td><td>{fnFormatCurrency(netITC.igst)}</td><td>{fnFormatCurrency(netITC.cgst)}</td><td>{fnFormatCurrency(netITC.sgst)}</td><td>{fnFormatCurrency(netITC.cess)}</td></tr>
                <tr><td>4D Ineligible ITC (informational)</td><td>{fnFormatCurrency(itcIneligibleTotal.igst)}</td><td>{fnFormatCurrency(itcIneligibleTotal.cgst)}</td><td>{fnFormatCurrency(itcIneligibleTotal.sgst)}</td><td>{fnFormatCurrency(itcIneligibleTotal.cess)}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ===================== TAB 3: TAX PAYMENT (5.1 & 6.1) ===================== */}
        {strActiveTab === "Payment" && (
          <div className="gstr1-panel">
            <h3 className="gstr1-panel-title">5.1 Interest &amp; late fee</h3>
            <HeadInputRow label="Interest payable" value={objInterest} onChange={setobjInterest} />
            <div className="row gstr1-btn-margin">
              <div className="col-sm-12 col-md-3"><label>Late fee</label></div>
              <div className="col-sm-6 col-md-2">
                <label style={{ fontSize: "11px" }}>CGST</label>
                <input type="number" className="form-control" value={objLateFee.cgst} onChange={(e) => setobjLateFee({ ...objLateFee, cgst: Number(e.target.value) || 0 })} />
              </div>
              <div className="col-sm-6 col-md-2">
                <label style={{ fontSize: "11px" }}>SGST</label>
                <input type="number" className="form-control" value={objLateFee.sgst} onChange={(e) => setobjLateFee({ ...objLateFee, sgst: Number(e.target.value) || 0 })} />
              </div>
            </div>
            <p className="gstr1-help-text">Late fee has no IGST/Cess leg under GST rules -- only CGST + SGST.</p>

            <hr />
            <h3 className="gstr1-panel-title">6.1 Payment of tax -- ITC set-off (Rule 88A order)</h3>
            <table className="gstr1-summary-table">
              <thead><tr><th></th><th>IGST</th><th>CGST</th><th>SGST</th><th>Cess</th></tr></thead>
              <tbody>
                <tr><td>Total liability</td><td>{fnFormatCurrency(payableByHead.igst)}</td><td>{fnFormatCurrency(payableByHead.cgst)}</td><td>{fnFormatCurrency(payableByHead.sgst)}</td><td>{fnFormatCurrency(payableByHead.cess)}</td></tr>
                <tr><td>ITC utilized</td><td>{fnFormatCurrency(setOff.itcUtilized.igst)}</td><td>{fnFormatCurrency(setOff.itcUtilized.cgst)}</td><td>{fnFormatCurrency(setOff.itcUtilized.sgst)}</td><td>{fnFormatCurrency(setOff.itcUtilized.cess)}</td></tr>
                <tr style={{ fontWeight: "bold" }}><td>Payable in cash</td><td>{fnFormatCurrency(setOff.cashPayable.igst)}</td><td>{fnFormatCurrency(setOff.cashPayable.cgst)}</td><td>{fnFormatCurrency(setOff.cashPayable.sgst)}</td><td>{fnFormatCurrency(setOff.cashPayable.cess)}</td></tr>
                <tr><td>ITC carried forward</td><td>{fnFormatCurrency(setOff.itcCarriedForward.igst)}</td><td>{fnFormatCurrency(setOff.itcCarriedForward.cgst)}</td><td>{fnFormatCurrency(setOff.itcCarriedForward.sgst)}</td><td>{fnFormatCurrency(setOff.itcCarriedForward.cess)}</td></tr>
              </tbody>
            </table>

            <h3 className="gstr1-panel-title">Cash ledger balance</h3>
            <div className="gstr1-alert-banner">
              <i className="fa-solid fa-triangle-exclamation"></i>
              Placeholder -- pending real Electronic Cash Ledger balance API from TaxPro/GSTN. Enter current balance manually until that's wired up.
            </div>
            <HeadInputRow label="Available balance" value={objCashLedger} onChange={setobjCashLedger} />

            <div className="gstr1-ack-box" style={{ marginTop: "16px" }}>
              <i className="fa-solid fa-money-bill-wave"></i>
              Net cash to pay now: <b>{fnFormatCurrency(netCashToPay)}</b>
              &nbsp;(tax {fnFormatCurrency(HEADS.reduce((s, h) => s + fnNum(setOff.cashPayable[h]), 0))} + interest/late fee {fnFormatCurrency(interestLateFeeTotal)} - ledger balance {fnFormatCurrency(totalCashLedger)})
            </div>
          </div>
        )}

        {/* ===================== TAB 4: PUSH TO GSTN ===================== */}
        {strActiveTab === "Push" && (
          <div className="gstr1-panel">
            <h3 className="gstr1-panel-title">Push data to GSTN</h3>
            <p className="gstr1-help-text">This will request an OTP on the registered mobile number linked to your GSTIN, then securely push the computed GSTR-3B summary to GSTN.</p>

            {arrValidationErrors.length > 0 && (
              <div className="gstr1-alert-banner">
                <i className="fa-solid fa-triangle-exclamation"></i>
                {arrValidationErrors.map((e, idx) => (<div key={idx}>{e.issue}</div>))}
              </div>
            )}

            <button className="btn-fabgreen" onClick={Request_OTP} disabled={arrValidationErrors.length > 0}>Push to GSTN&nbsp;<i className="fa-solid fa-paper-plane"></i></button>
            {arrValidationErrors.length > 0 && (<p className="gstr1-error-text">Fix the Table 3.1 head split in tab 1 first.</p>)}
          </div>
        )}

        {/* ===================== TAB 5: FILE RETURN ===================== */}
        {strActiveTab === "File" && (
          <div className="gstr1-panel">
            <h3 className="gstr1-panel-title">File your return</h3>
            <table className="gstr1-summary-table">
              <thead><tr><th></th><th>IGST</th><th>CGST</th><th>SGST</th><th>Cess</th></tr></thead>
              <tbody>
                <tr><td>Tax payable in cash</td><td>{fnFormatCurrency(setOff.cashPayable.igst)}</td><td>{fnFormatCurrency(setOff.cashPayable.cgst)}</td><td>{fnFormatCurrency(setOff.cashPayable.sgst)}</td><td>{fnFormatCurrency(setOff.cashPayable.cess)}</td></tr>
                <tr><td>Interest</td><td>{fnFormatCurrency(objInterest.igst)}</td><td>{fnFormatCurrency(objInterest.cgst)}</td><td>{fnFormatCurrency(objInterest.sgst)}</td><td>{fnFormatCurrency(objInterest.cess)}</td></tr>
                <tr><td>Late fee</td><td>-</td><td>{fnFormatCurrency(objLateFee.cgst)}</td><td>{fnFormatCurrency(objLateFee.sgst)}</td><td>-</td></tr>
              </tbody>
            </table>

            {strAckNo && (
              <div className="gstr1-ack-box">
                <i className="fa-solid fa-circle-check"></i>
                Filed successfully. Acknowledgement No: <b>{strAckNo}</b>
              </div>
            )}

            <button className="btn-fabgreen gstr1-btn-margin" onClick={File_Return} disabled={strFilingStatus !== "Pushed"}>File Return&nbsp;<i className="fa-solid fa-check"></i></button>
            {strFilingStatus !== "Pushed" && (<p className="gstr1-error-text">Push data to GSTN first (tab 4) before filing.</p>)}
          </div>
        )}
      </div>

      {isOTPModal && (
        <div className="gstr1-modal-overlay">
          <div className="gstr1-modal">
            <div className="popup-header">
              <p>Verify OTP</p>
              <i className="fas fa-times" onClick={() => setisOTPModal(false)}></i>
            </div>
            <div className="popup-body">
              <p className="gstr1-help-text">Enter the OTP sent to your GSTN registered mobile number.</p>
              <input type="text" className="form-control" maxLength={6} value={strOTP} onChange={(e) => setstrOTP(e.target.value)} placeholder="Enter 6-digit OTP" />
            </div>
            <div className="popup-footer">
              <button className="btn-fabgreen" onClick={Verify_OTP_PushGSTN}>Verify &amp; Push&nbsp;<i className="fa-solid fa-check"></i></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Gstr3b;